import {
  displayObInvestorLabel,
  displayObProductName
} from '@/utils/pricing/obProductLabels';

const PICKUP_CHANNEL_SUFFIX = /\s+-\s+(?:Correspondent|Non-Delegated|Wholesale|Core)(?:\s+-\s*.*|\s*)$/i;
const PICKUP_LEGAL_SUFFIX = /(?:,\s*|\s+)(?:Inc(?:orporated)?\.?|L\.?L\.?C\.?|Corp(?:oration)?\.?)(?=\s*$)/i;

function stripPickupLegalSuffixes(value: string): string {
  let result = value;
  let previous = '';
  while (result !== previous) {
    previous = result;
    result = result.replace(PICKUP_LEGAL_SUFFIX, '').trim();
  }
  return result;
}

/**
 * Display-only investor cleanup for the dense Pick Up workspace. Stored values,
 * comparison keys, audit records, and action payloads must continue using raw names.
 */
export function formatPickupInvestorDisplayName(value: string | null | undefined): string {
  const parsed = displayObInvestorLabel(value)
    .replace(PICKUP_CHANNEL_SUFFIX, '')
    .replace(/(?:\s+-\s*)+$/g, '')
    .trim();
  return stripPickupLegalSuffixes(parsed);
}

export function formatPickupInvestorRoute(
  currentInvestor: string | null | undefined,
  bestInvestor: string | null | undefined
): string {
  const current = formatPickupInvestorDisplayName(currentInvestor) || 'Unknown investor';
  const best = formatPickupInvestorDisplayName(bestInvestor);
  return best && best.toLocaleLowerCase() !== current.toLocaleLowerCase()
    ? `${current} → ${best}`
    : current;
}

function normalizePickupProductVocabulary(value: string): string {
  return value
    .replace(/\b(\d{1,2})\s*(?:year|yr)s?\b/gi, '$1yr')
    .replace(/\s+-\s+([^\s].*)$/, ' · $1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Display-only product cleanup for the dense investor-pricing surfaces. The
 * raw vendor program remains available on the product record and in the title.
 */
export function formatPickupProductDisplayName(
  productName: string | null | undefined,
  investor: string | null | undefined
): string {
  const product = normalizePickupProductVocabulary(
    displayObProductName(productName, investor)
  );
  if (!product) {
    return 'Product unavailable';
  }

  const agencyProduct = /\b(?:FNMA|FHLMC|Conforming|Conventional|Agency)\b/i.test(product);
  const term = product.match(/\b(\d{1,2}yr)\b/i)?.[1]?.toLowerCase();
  const amortization = /\bFixed\b/i.test(product)
    ? 'Fixed'
    : product.match(/\b(?:ARM|Adjustable)\b/i)?.[0];
  if (!agencyProduct || !term || !amortization) {
    return product;
  }

  const explicitQualifier = product.match(/\b(Home Possible|HomeReady)\b/i)?.[1]
    || product.match(/\bFixed\b\s*·\s*(.+)$/i)?.[1]
    || 'Agency';
  return `Conv ${term} ${amortization} · ${explicitQualifier}`;
}
